import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { endpoints } from './api';
import { groupOf, visibleGroups } from './nav';
import { useFeatures } from './features';
import { usePermissions, useSession } from './session';
import { Button } from './ui';
import { GlobalSearch } from './components/GlobalSearch';
import { PLATFORM_NAME } from '@bre-ticketing/contracts';

/**
 * The shell: dark navigation, bright content (apps/admin/README.md).
 *
 * The menu is grouped by job and shows each person only what they may open;
 * what is in it, and why, is in nav.ts (D-325).
 */

/* The menu itself lives in nav.ts (D-325). */

export default function App() {
  const session = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const signOut = useMutation({
    mutationFn: endpoints.logout,
    onSettled: async () => {
      queryClient.clear();
      await navigate('/login', { replace: true });
    },
  });

  return (
    <div className="flex min-h-full">
      <nav className="flex w-56 shrink-0 flex-col bg-shell-900 text-white">
        <div className="border-b border-shell-700 px-5 py-4">
          {/* The product's name, from the one place it is written (D-123). */}
          <p className="font-display text-lg leading-none font-bold tracking-tight">{PLATFORM_NAME}</p>
          <p className="mt-1 text-xs tracking-widest text-shell-500 uppercase">Admin</p>
        </div>

        <GlobalSearch />
        <Menu />

        <div className="border-t border-shell-700 px-5 py-4">
          <p className="truncate text-sm font-medium">{session.data?.staff.display_name ?? '—'}</p>
          <p className="text-xs text-shell-500">
            {session.data?.session.auth_method === 'PIN'
              ? 'Signed in with PIN'
              : session.data?.session.auth_method === 'GOOGLE_OIDC'
                ? 'Signed in with Google'
                : 'Signed in with an emailed link'}
          </p>
          <button
            type="button"
            onClick={() => signOut.mutate()}
            disabled={signOut.isPending}
            className="mt-2 text-xs text-white/60 underline underline-offset-2 hover:text-white disabled:opacity-50"
          >
            {signOut.isPending ? 'Signing out…' : 'Sign out'}
          </button>
        </div>
      </nav>

      <main className="min-w-0 flex-1">
        <div className="mx-auto max-w-6xl p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

/** Exported for pages that need a consistent "not permitted" state. */
export function NotPermitted({ permission }: { permission: string }) {
  return (
    <div className="rounded-lg border border-line bg-surface px-5 py-10 text-center">
      <p className="text-sm text-ink-600">You do not have permission to view this.</p>
      <p className="mt-1 text-xs text-ink-400">Requires {permission}</p>
      <Button className="mt-4" onClick={() => window.history.back()}>
        Go back
      </Button>
    </div>
  );
}

const COLLAPSED_KEY = 'admin.nav.collapsed';

/** Which groups this browser has folded away. A convenience only: it can come back empty. */
function readCollapsed(): Set<string> {
  try {
    const raw = window.localStorage.getItem(COLLAPSED_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

/**
 * The grouped menu (D-325). Only the links this person may use, and only the
 * groups with something left in them. The group holding the current page is
 * always open, whatever was folded, so nobody loses the page they are on.
 */
function Menu() {
  const can = usePermissions();
  const features = useFeatures();
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState<Set<string>>(readCollapsed);
  const current = groupOf(pathname);

  const toggle = (id: string) => {
    setCollapsed((before) => {
      const next = new Set(before);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      try {
        window.localStorage.setItem(COLLAPSED_KEY, JSON.stringify([...next]));
      } catch {
        // Private windows and blocked storage: the menu still works, it just forgets.
      }
      return next;
    });
  };

  return (
    <div className="flex-1 space-y-3 overflow-y-auto p-2">
      {visibleGroups(can, features).map((group) => {
        const open = group.id === current || !collapsed.has(group.id);
        return (
          <section key={group.id} aria-labelledby={`nav-${group.id}`}>
            <button
              type="button"
              id={`nav-${group.id}`}
              onClick={() => toggle(group.id)}
              aria-expanded={open}
              disabled={group.id === current}
              className="flex w-full items-center justify-between px-3 py-1 text-xs font-semibold tracking-widest text-shell-500 uppercase hover:text-white disabled:cursor-default disabled:hover:text-shell-500"
            >
              {group.label}
              <span aria-hidden="true">{open ? '−' : '+'}</span>
            </button>
            {open ? (
              <ul className="mt-0.5 space-y-0.5">
                {group.items.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.end}
                      className={({ isActive }) =>
                        `block rounded px-3 py-1.5 text-sm transition-colors ${
                          isActive ? 'bg-shell-700 font-medium text-white' : 'text-white/70 hover:bg-shell-800 hover:text-white'
                        }`
                      }
                    >
                      {item.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            ) : null}
          </section>
        );
      })}
    </div>
  );
}
